import { isFullyLoanedOut } from './formats';
import type { Movie, Watch } from './types';

export type RecommendationReason = 'never-watched' | 'long-ago';

export interface Recommendation {
  movie: Movie;
  reason: RecommendationReason;
  /** Null when the movie has never been watched. */
  daysSinceLastWatch: number | null;
  daysOwned: number;
  /** Your own average rating for this movie, when you have rated it. */
  averageRating: number | null;
  score: number;
}

export interface RecommendOptions {
  now?: Date;
  limit?: number;
  /**
   * A movie watched more recently than this is not a candidate. Six months is
   * long enough that a rewatch feels like a treat rather than a repeat.
   */
  minDaysSinceWatch?: number;
  /** Include movies whose every copy is currently lent out. Off by default —
   *  you cannot watch a disc that is at a friend's place. */
  includeLoanedOut?: boolean;
}

const MS_PER_DAY = 86_400_000;

function daysBetween(from: string, now: Date): number {
  const start = new Date(from).getTime();
  if (Number.isNaN(start)) return 0;
  return Math.max(0, Math.floor((now.getTime() - start) / MS_PER_DAY));
}

/** Mean of your own ratings, per movie. Unrated watches are ignored. */
export function averageRatingByMovie(watches: Watch[]): Map<string, number> {
  const totals = new Map<string, { sum: number; count: number }>();
  for (const watch of watches) {
    if (typeof watch.rating !== 'number') continue;
    const entry = totals.get(watch.movieId) ?? { sum: 0, count: 0 };
    entry.sum += watch.rating;
    entry.count += 1;
    totals.set(watch.movieId, entry);
  }
  const averages = new Map<string, number>();
  for (const [movieId, { sum, count }] of totals) {
    if (count > 0) averages.set(movieId, sum / count);
  }
  return averages;
}

/**
 * What to put in the player tonight.
 *
 * Two things earn a recommendation: you bought it and never played it, or you
 * loved it once and it has been a long time. Never-watched always outranks a
 * rewatch, and within that group the disc that has been sitting unopened
 * longest comes first — that is the one nagging at you.
 */
export function recommend(
  movies: Movie[],
  watches: Watch[],
  options: RecommendOptions = {},
): Recommendation[] {
  const now = options.now ?? new Date();
  const minDays = options.minDaysSinceWatch ?? 180;
  const includeLoanedOut = options.includeLoanedOut ?? false;
  const ratings = averageRatingByMovie(watches);

  const results: Recommendation[] = [];

  for (const movie of movies) {
    if (!includeLoanedOut && isFullyLoanedOut(movie)) continue;

    const daysOwned = daysBetween(movie.addedAt, now);
    const averageRating = ratings.get(movie.id) ?? null;

    if (movie.watchCount === 0 || movie.lastWatchedAt === null) {
      results.push({
        movie,
        reason: 'never-watched',
        daysSinceLastWatch: null,
        daysOwned,
        averageRating,
        // The +1000 floor keeps every unplayed disc above every rewatch.
        score: 1000 + daysOwned,
      });
      continue;
    }

    const daysSinceLastWatch = daysBetween(movie.lastWatchedAt, now);
    if (daysSinceLastWatch < minDays) continue;

    // A movie you rated five stars deserves to come back sooner than one you
    // rated two, so the gap is stretched or compressed by up to ~30%.
    const multiplier =
      averageRating === null ? 1 : Math.min(1.4, Math.max(0.7, 1 + (averageRating - 3) * 0.12));

    results.push({
      movie,
      reason: 'long-ago',
      daysSinceLastWatch,
      daysOwned,
      averageRating,
      score: daysSinceLastWatch * multiplier,
    });
  }

  results.sort((a, b) => b.score - a.score);
  const limit = options.limit ?? results.length;
  return results.slice(0, limit);
}

/**
 * Pick one recommendation at random, weighted by score, from the strongest
 * candidates. Weighting keeps "Surprise me" pointed at things worth watching
 * without returning the same disc every time.
 */
export function surpriseMe(
  recommendations: Recommendation[],
  random: () => number = Math.random,
  poolSize = 25,
): Recommendation | null {
  const pool = recommendations.slice(0, poolSize);
  if (pool.length === 0) return null;

  const total = pool.reduce((sum, item) => sum + Math.max(item.score, 1), 0);
  let threshold = random() * total;
  for (const item of pool) {
    threshold -= Math.max(item.score, 1);
    if (threshold <= 0) return item;
  }
  return pool[pool.length - 1] ?? null;
}
